import type { PropertyFeatureFlags } from "@/api/properties";

// What a property's deal entitles the signed-in user to see, from the feature
// toggles on the portfolio summary. Sales and revenue are not exclusive.

export type Capability = "sales" | "revenue";

export const ALL_CAPABILITIES: readonly Capability[] = ["sales", "revenue"];

/**
 * Revman grants revenue; proactive or reactive grants sales. Falls open per
 * capability: a toggle the backend omitted is unknown, and an unknown must
 * never hide a surface the customer is actually paying for.
 */
export const capabilitiesForProperty = (
  flags: PropertyFeatureFlags
): readonly Capability[] => {
  const { isRevmanEnabled, isProactiveEnabled, isReactiveEnabled } = flags;
  const salesUnknown = isProactiveEnabled == null && isReactiveEnabled == null;
  const hasSales = salesUnknown || !!isProactiveEnabled || !!isReactiveEnabled;
  const hasRevenue = isRevmanEnabled == null || isRevmanEnabled;
  return ALL_CAPABILITIES.filter((c) =>
    c === "sales" ? hasSales : hasRevenue
  );
};

const CAPABILITY_BY_SCOPE: Record<string, Capability> = {
  SALES: "sales",
  PROACTIVE_SALES: "sales",
  REACTIVE_SALES: "sales",
  REVENUE: "revenue",
};

/** Maps the token's `user_org_scopes` to capabilities. Fails closed: no scopes, no capabilities. */
export const capabilitiesForScopes = (
  scopes: readonly string[] | undefined
): ReadonlySet<Capability> =>
  new Set(
    (scopes ?? [])
      // Hyphens folded to underscores so "proactive-sales" and "proactive_sales" both match.
      .map(
        (s) => CAPABILITY_BY_SCOPE[s.trim().toUpperCase().replace(/-/g, "_")]
      )
      .filter((c): c is Capability => !!c)
  );
